import { Clock4, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntryFormDialog } from '@/components/entries/EntryFormDialog';
import { TimerStartButtons } from '@/components/timer/TimerStartButtons';
import { useTimerActions } from '@/components/timer/TimerActionsProvider';
import { Button } from '@/components/ui/button';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { PhoneAlarmWarning } from '@/components/alarms/PhoneAlarmWarning';
import { useActiveShift, useStartOptions } from '@/hooks/use-shifts';
import { SHORTCUT_HINTS } from '@/lib/keyboard-shortcuts';

type NextDialog = 'details' | 'log' | null;

/**
 * Phone "+" (original QuickAddSheet): Start timer | Start with details…, then Log time.
 * The follow-up dialog opens from onCloseAutoFocus, i.e. after the sheet has fully closed and released
 * its focus trap, so two Radix modals never overlap and `pointer-events: none` is never left on <body>.
 */
export function QuickAddSheet(): JSX.Element {
  const actions = useTimerActions();
  const [open, setOpen] = useState(false);
  const [next, setNext] = useState<NextDialog>(null);
  const [logOpen, setLogOpen] = useState(false);
  // Sprint 13 (§15 Phone): "Start shift" when start options exist, "End shift" while on shift. Both open the
  // Dashboard Shift card, which owns the location choice, the stop-timer prompt and the early-end confirm.
  const navigate = useNavigate();
  const onShift = Boolean(useActiveShift()?.shift);
  const options = useStartOptions(open && !onShift);
  const shiftLabel = onShift ? 'End shift' : (options.data?.length ?? 0) > 0 ? 'Start shift' : null;

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            aria-label="Quick add"
            className="-mt-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          >
            <Plus aria-hidden="true" className="h-[22px] w-[22px] [stroke-width:2]" />
          </button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          showClose={false}
          className="p-3"
          onCloseAutoFocus={(event) => {
            if (next === null) return;
            event.preventDefault();
            if (next === 'details') actions.openStartWithDetails();
            else setLogOpen(true);
            setNext(null);
          }}
        >
          <div className="flex items-center justify-between px-1 pb-2">
            <SheetTitle className="text-xs font-medium text-muted-foreground">Quick add</SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Close quick add">
                <X />
              </Button>
            </SheetClose>
          </div>
          <SheetDescription className="sr-only">Start a timer or log time.</SheetDescription>
          <div className="px-1 pb-1">
            <TimerStartButtons
              variant="full"
              onStartInstant={() => {
                actions.startInstant();
                setOpen(false);
              }}
              onStartWithDetails={() => {
                setNext('details');
                setOpen(false);
              }}
            />
          </div>
          {shiftLabel && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/');
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Clock4 aria-hidden="true" className="h-[18px] w-[18px] text-muted-foreground" /> {shiftLabel}
            </button>
          )}
          {/* Sprint 14 (§8.4): alarms don't ring on phones; Start stays allowed. Sprint 15: the SOP opens in a popover. */}
          <div className="px-1">
            <PhoneAlarmWarning show={!onShift && (options.data ?? []).some((o) => o.alarmsEnabled)} sopPopover />
          </div>
          <button
            type="button"
            onClick={() => {
              setNext('log');
              setOpen(false);
            }}
            aria-keyshortcuts={SHORTCUT_HINTS['log-time'].aria}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus aria-hidden="true" className="h-[18px] w-[18px] text-muted-foreground" /> Log time
          </button>
        </SheetContent>
      </Sheet>
      <EntryFormDialog open={logOpen} onOpenChange={setLogOpen} mode="create" />
    </>
  );
}
